import { useCallback } from 'react';
import { switchcase, useNoticeBannersStackContext } from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { useCreditCardTelemetry } from './CreditCardForm.hooks.telemetry';

// ──────────────────────── Error Handling & Telemetry ────────────────────────
export const useCreditCardErrorHandling = () => {
  const extractUserError = useUserError();
  const { monitoredOnFail } = useCreditCardTelemetry({ skipMountEvent: true });
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  return useCallback(
    (props: HandleErrorProps) => {
      const userError = extractUserError(props);

      monitoredOnFail(userError, props.systemError);
      addNoticeBanner({
        text: userError,
        palette: 'caution',
      });
    },
    [extractUserError, monitoredOnFail, addNoticeBanner],
  );
};

const useUserError = () => {
  const { t } = useLocalizationContext();

  return useCallback(
    (props: HandleErrorProps) => {
      // Localization of known errors, fallback to remote user error.
      if (props.errorMessage) {
        return switchcase({
          InvalidSubmittedBillingAccount: t(
            'credit-card-form.error.billing.invalid',
          ),
          InvalidPostalCode: t('credit-card-form.error.zipCode.invalid'),
          TooManyRequests: t('credit-card-form.error.too-many-requests'),
        })(props.errorMessage)(props.typename ?? '');
      }

      // Generic error when deleting a credit card.
      if (props.isDeleteRequest) {
        return t('credit-card-form.error.delete');
      }

      // Generic error when submitting a credit card.
      return t('credit-card-form.error.save');
    },
    [t],
  );
};

type HandleErrorProps = {
  readonly typename?: string;
  readonly errorMessage?: string;
  readonly systemError?: string;
  readonly isDeleteRequest?: boolean;
};
